import { motion } from "framer-motion";
import { fadeInUp, staggerContainer } from "@/motion/variants";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { PADMORA_EIN, PADMORA_FORMATION_STATE, PADMORA_LEGAL_NAME } from "@/lib/constants";
import { TradingTrustStrip } from '@/components/TradingTrustStrip';
import SEO from "@/components/SEO";
import { TradingPartnersTrustBar } from "@/components/TrustBar";

const About = () => {
  const team = [
    {
      name: "Michel Padron",
      role: "CEO & Trading Technology Leader",
      description: "Leading AI-powered commodity trading operations with bank-grade compliance and document control, bringing 15+ years of technology and business development expertise.",
      photo: "/michel-profile.gif",
      linkedin: "https://www.linkedin.com/in/michelpadron/"
    },
    {
      name: "Ileana Morales",
      role: "CCO & International Trading Expert",
      description: "Bringing deep expertise in international trade operations, compliance, and relationship management across global commodity markets.",
      photo: "/ileana-profile.gif",
      linkedin: "https://www.linkedin.com/in/ileana-morales-diaz/"
    },
    {
      name: "Jonas Wohnfurter",
      role: "Business Development & Account Manager",
      description: "Driving partnership development and account management with a focus on building lasting relationships in the commodities ecosystem.",
      photo: "/jonas-profile.png",
      linkedin: "https://www.linkedin.com/in/jonas-wohnfurter-5a0898218/"
    }
  ];

  return (
    <div className="min-h-screen pt-6">
      <SEO
        title="About Padmora | Specialised Trading Firm | Team & Leadership"
        description="US-based boutique trading firm founded in 2025, specializing in LC-backed commodity deals with AI-powered execution and bank-grade compliance. Meet our expert team led by Michel Padron Morales."
        image="/og-about.jpg"
        canonical="https://padmora.com/about"
        keywords={[
          "Padmora team",
          "US trading firm",
          "commodity trading experts",
          "AI trading specialists",
          "trade finance professionals",
          "compliance experts",
          "business development",
          "international trading",
          "commodity finance",
          "Michel Padron Morales",
          "Wyoming LLC",
          "EIN registered"
        ]}
        aiOptimization={{
          aiKeywords: [
            "experienced trading professionals",
            "AI technology experts",
            "compliance specialists",
            "international trade expertise",
            "commodity market knowledge"
          ],
          entities: [
            "Michel Padron Morales",
            "United States",
            "Wyoming LLC",
            "Commodity Trading",
            "AI Technology",
            "Trade Finance"
          ],
          topics: [
            "Company Leadership",
            "Trading Expertise",
            "AI Innovation",
            "Compliance Management",
            "Business Development"
          ]
        }}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "About Padmora",
          "description": "US boutique trading firm specializing in AI-powered commodity deals with LC-backed financing and full compliance automation.",
          "mainEntity": {
            "@type": "Organization",
            "name": "Padmora",
            "foundingDate": "2025",
            "founder": {
              "@type": "Person",
              "name": "Michel Padron Morales",
              "jobTitle": "CEO & Trading Technology Leader",
              "description": "Leading AI-powered commodity trading operations with bank-grade compliance and document control, bringing 15+ years of technology and business development expertise.",
              "sameAs": "https://www.linkedin.com/in/michelpadron/"
            },
            "employee": [
              {
                "@type": "Person",
                "name": "Michel Padron Morales",
                "jobTitle": "CEO & Trading Technology Leader",
                "description": "Leading AI-powered commodity trading operations with bank-grade compliance and document control, bringing 15+ years of technology and business development expertise.",
                "sameAs": "https://www.linkedin.com/in/michelpadron/"
              },
              {
                "@type": "Person",
                "name": "Ileana Morales",
                "jobTitle": "CCO & International Trading Expert",
                "description": "Bringing deep expertise in international trade operations, compliance, and relationship management across global commodity markets.",
                "sameAs": "https://www.linkedin.com/in/ileana-morales-d%C3%ADaz-064806100/"
              },
              {
                "@type": "Person",
                "name": "Jonas Wohnfurter",
                "jobTitle": "Business Development & Account Manager",
                "description": "Driving partnership development and account management with a focus on building lasting relationships in the commodities ecosystem.",
                "sameAs": "https://www.linkedin.com/in/jonas-wohnfurter-5a0898218/"
              }
            ],
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "US",
              "addressRegion": "Wyoming"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "email": "hello@padmora.com"
            }
          }
        }}
        breadcrumbs={[
          { name: "Home", url: "https://padmora.com/" },
          { name: "About", url: "https://padmora.com/about" }
        ]}
      />
      <Navigation />

      {/* Hero Section */}
      <section className="min-h-screen flex flex-col justify-center">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto text-center"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-6xl font-bold text-slate-900 mb-6"
            >
              Specialised Trading Firm
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-slate-600"
            >
              LC-backed commodity deals with AI-powered execution and bank-grade compliance
            </motion.p>
          </motion.div>

          <motion.div variants={fadeInUp}>
            <TradingTrustStrip />
          </motion.div>

          {/* Partners at bottom of hero */}
          <div className="mt-16">
            <TradingPartnersTrustBar />
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.h2
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="text-3xl font-bold text-center mb-12"
          >
            Our Team
          </motion.h2>
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto"
          >
            {team.map((member, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-card/50 border border-border/50 p-8 rounded-lg text-center hover:shadow-md transition-shadow"
              >
                <div className="w-32 h-32 rounded-full overflow-hidden mx-auto mb-4 border-2 border-blue-100">
                  <img
                    src={member.photo}
                    alt={`${member.name} profile photo`}
                    className={`w-full h-full object-cover ${
                      member.name === "Ileana Morales" 
                        ? "scale-125 translate-y-1" 
                        : ""
                    }`}
                    loading="lazy"
                    onError={(e) => {
                      console.error('Failed to load profile image:', e);
                    }}
                  />
                </div>
                <h3 className="text-xl font-bold mb-2">{member.name}</h3>
                <h4 className="text-blue-600 font-semibold mb-4">{member.role}</h4>
                <p className="text-slate-600 mb-4">{member.description}</p>
                <a
                  href={member.linkedin}
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
                  </svg>
                  LinkedIn
                </a>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Company Information */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-2xl font-bold text-center mb-8"
            >
              Company Information
            </motion.h2>
            <motion.div
              variants={fadeInUp}
              className="bg-white border border-border/50 rounded-lg p-8 shadow-sm"
            >
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <span className="font-semibold text-slate-900">Legal name:</span>
                    <span className="ml-2 text-slate-600">{PADMORA_LEGAL_NAME}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900">EIN:</span>
                    <span className="ml-2 text-slate-600">{PADMORA_EIN}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900">State of formation:</span>
                    <span className="ml-2 text-slate-600">{PADMORA_FORMATION_STATE}, USA</span>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <span className="font-semibold text-slate-900">Banking Relationships:</span>
                    <span className="ml-2 text-slate-600">US institutions (details shared post-KYC under NDA)</span>
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900">Operating Standards:</span>
                    <span className="ml-2 text-slate-600">ICC UCP600 / ISP98</span>
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900">Business Focus:</span>
                    <span className="ml-2 text-slate-600">LC-backed commodity trading</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Compliance & Standards */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto text-center"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold mb-8"
            >
              Compliance & Standards
            </motion.h2>
            <motion.div
              variants={fadeInUp}
              className="grid md:grid-cols-3 gap-6 mb-12"
            >
              <div className="bg-card/50 border border-border/50 p-6 rounded-lg">
                <div className="text-xl font-bold text-blue-600 mb-2">KYC/AML</div>
                <div className="text-slate-600">Comprehensive customer verification and anti-money laundering procedures</div>
              </div>
              <div className="bg-card/50 border border-border/50 p-6 rounded-lg">
                <div className="text-xl font-bold text-blue-600 mb-2">Sanctions Screening</div>
                <div className="text-slate-600">OFAC, EU, and UN sanctions compliance for all counterparties</div>
              </div>
              <div className="bg-card/50 border border-border/50 p-6 rounded-lg">
                <div className="text-xl font-bold text-blue-600 mb-2">LC Compliance</div>
                <div className="text-slate-600">UCP600 and ISP98 standards for all letter of credit operations</div>
              </div>
            </motion.div>
            <motion.p
              variants={fadeInUp}
              className="text-slate-600 text-sm"
            >
              All transactions include e-signature processes with complete audit trails.
              Banking relationships disclosed post-KYC under NDA.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-2xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-slate-900 mb-6"
            >
              Ready to Connect?
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-slate-600 mb-8"
            >
              Send an enquiry to michel@padmora.com to discuss your commodity trading needs.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Button
                size="lg"
                className="px-8 py-4 text-lg bg-primary hover:bg-primary/90 text-white"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com"
                >
                  Send enquiry
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default About;
